import type { MarketingRoleSlug } from './role-page-model';
import type { MarketingRolePageSource } from './role-page-content.types';

type FinalRoleSlug = Extract<
  MarketingRoleSlug,
  | 'marketing-ecommerce'
  | 'merchandisers-buyers'
  | 'sourcing-supply-chain'
  | 'manufactures-factories'
>;

export const MARKETING_ROLE_PAGE_CONTENT_B = {
  'marketing-ecommerce': {
    heroBody: 'Launch faster. Convert more. Keep every channel in sync.',
    composerPlaceholder: 'What are you launching?',
    useCases: [
      {
        label: 'Launch Products Faster',
        prompt:
          'Create a product launch content plan that reduces handoff delays. Map required assets, dependencies, owners, approval steps, and channel deadlines into a practical launch checklist. Ask about my product, audience, brand voice, launch date, channels, and available assets before planning the content. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Build Better Product Pages',
        prompt:
          'Build a product page from approved product information. Draft benefit-led copy, specifications, sizing guidance, image briefs, and FAQs without inventing features or unsupported claims. Ask about my product, audience, brand voice, launch date, channels, and available assets before planning the content. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Create Video for Every Channel',
        prompt:
          'Plan product videos for each of my marketing channels. Create hooks, scripts, shot lists, aspect ratios, captions, and calls to action using the available assets. Ask about my product, audience, brand voice, launch date, channels, and available assets before planning the content. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Keep Catalogs in Sync',
        prompt:
          'Audit my product catalogs across sales channels. Identify mismatched descriptions, variants, prices, and assets, then propose a field mapping and review process for updates. Ask about my product, audience, brand voice, launch date, channels, and available assets before planning the content. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Localize Content by Market',
        prompt:
          'Adapt my product content for different markets. Localize language, sizing, units, cultural context, and channel requirements while keeping verified product facts and brand voice consistent. Ask about my product, audience, brand voice, launch date, channels, and available assets before planning the content. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Turn Performance into Variants',
        prompt:
          'Use my campaign performance data to propose new creative variants. Identify testable hypotheses, vary hooks or visuals, and define success metrics without claiming unsupported causality. Ask about my product, audience, brand voice, launch date, channels, and available assets before planning the content. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
    ],
    whyCards: [
      {
        title: 'Launch Without Delays',
        body: 'Move products from approved to live without rebuilding content for every channel.',
        imageAlt: 'A complete product launch kit prepared across channels',
      },
      {
        title: 'Improve Product Conversion',
        body: 'Give customers accurate information, richer product media, and stronger selling content.',
        imageAlt: 'A product page with accurate data, imagery, and video',
      },
      {
        title: 'Scale What Performs',
        body: 'Use commerce and campaign results to guide the next creative variations.',
        imageAlt: 'Performance results informing new creative variants',
      },
    ],
    insights: [
      {
        title: 'Product Page Builder',
        body: 'Create titles, descriptions, benefits, SEO copy, size details, care information, and product stories.',
      },
      {
        title: 'Image and Video Studio',
        body: 'Create product, detail, on-model, lifestyle, campaign, marketplace, demonstration, and short-form assets.',
      },
      {
        title: 'Performance-to-Creative Loop',
        body: 'Identify winning products, messages, and formats, then create the next variations.',
      },
    ],
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Which platforms can IMAI connect to?',
          answer:
            'IMAI can connect with Shopify and supported marketplace, shopping, advertising, and marketing platforms. Availability depends on your setup.',
        },
        {
          question: 'Can IMAI create product videos?',
          answer:
            'Yes. Approved product assets can become demonstrations, social videos, ads, and launch content for review before publishing.',
        },
        {
          question: 'Can content be published directly?',
          answer:
            'Approved content can be sent to connected platforms where direct publishing is supported. Your team controls approval, scheduling, and release.',
        },
        {
          question: 'Does IMAI replace Shopify or our marketing tools?',
          answer:
            'No. Commerce and marketing platforms remain the systems of record and distribution while IMAI connects product data to content creation and launch.',
        },
      ],
    },
    cta: {
      eyebrow: 'From product data to revenue',
      heading: 'Take every product from approved to live.',
      cards: [
        { label: 'Build Your Next Launch', href: '/auth?mode=signup' },
        { label: 'Book a Commerce Demo', href: '/contact' },
      ],
    },
  },
  'merchandisers-buyers': {
    heroBody: 'Build the range customers will choose.',
    composerPlaceholder: 'Build any range with IMAI',
    useCases: [
      {
        label: 'Turn Trends Into Assortments',
        prompt:
          'Translate relevant trends into a commercial assortment. Connect trend signals to customer needs, categories, price points, and planned depth, then recommend a balanced buying direction. Ask about my customer, market, season, sales history, category mix, buying budget, and margin targets before planning the range. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Build Ranges by Market',
        prompt:
          'Build an assortment for my target markets. Adapt category mix, sizing, seasonality, price architecture, and buying depth to local demand while showing the underlying assumptions. Ask about my customer, market, season, sales history, category mix, buying budget, and margin targets before planning the range. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Balance Styles, Colors and Prices',
        prompt:
          'Balance styles, colors, and prices across my range. Review SKU counts and category roles, identify overconcentration, and propose a mix aligned with budget and margin. Ask about my customer, market, season, sales history, category mix, buying budget, and margin targets before planning the range. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Spot Gaps and Duplication',
        prompt:
          'Review my assortment for gaps and duplication. Compare customer needs, occasions, categories, price points, and similar styles, then recommend what to add, combine, or remove. Ask about my customer, market, season, sales history, category mix, buying budget, and margin targets before planning the range. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Compare Cost and Margin',
        prompt:
          'Compare product cost and margin scenarios for my buying plan. Include landed costs, selling prices, discounts, and order quantities, and show which assumptions drive profitability. Ask about my customer, market, season, sales history, category mix, buying budget, and margin targets before planning the range. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Create Buyer-Ready Line Sheets',
        prompt:
          'Create buyer-ready line sheets using approved product data. Organize styles, images, colorways, sizes, wholesale prices, minimum orders, and delivery windows, and flag missing commercial details. Ask about my customer, market, season, sales history, category mix, buying budget, and margin targets before planning the range. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
    ],
    whyCards: [
      {
        title: 'Know Why Every SKU Exists',
        body: 'Give every product a clear customer, category, price point, and role within the assortment.',
        imageAlt: 'A range organized by customer, category, price, and role',
      },
      {
        title: 'Pressure-Test Before Sampling',
        body: 'Compare products, colors, materials, and costs while the range can still change quickly.',
        imageAlt:
          'Products, colorways, materials, and costs compared before sampling',
      },
      {
        title: 'Walk Into Line Review Ready',
        body: 'Bring the complete range, commercial logic, and recommended decisions into one visual review.',
        imageAlt: 'A complete visual assortment prepared for line review',
      },
    ],
    insights: [
      {
        title: 'Visual Assortment Builder',
        body: 'Organize the range by category, product type, delivery, color family, price point, and market.',
      },
      {
        title: 'Gap, Duplication, and Margin View',
        body: 'Spot missing products and repetition while seeing expected cost, retail price, and target margin.',
      },
      {
        title: 'Buyer Presentation Builder',
        body: 'Create line sheets and presentations with imagery, colorways, materials, costs, prices, and market selections.',
      },
    ],
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Can we start with our existing range plan?',
          answer:
            'Yes. Begin with current categories, styles, colorways, price points, markets, and targets, then turn the plan into a visual assortment.',
        },
        {
          question: 'Can IMAI decide which products belong in the range?',
          answer:
            'IMAI helps compare products, gaps, duplication, and commercial logic. Final selection remains a merchant and buyer decision.',
        },
        {
          question: 'Can we see cost and margin while reviewing products?',
          answer:
            'Yes. Expected cost, planned retail, and target margin can remain connected while teams compare materials, colorways, and alternatives.',
        },
        {
          question: 'Can we build different ranges for different markets?',
          answer:
            'Yes. Start with one master assortment, then adjust products, colorways, prices, and emphasis for regions, partners, stores, or digital channels.',
        },
      ],
    },
    cta: {
      eyebrow: 'Every product with a clear commercial role',
      heading: 'Build a range worth backing.',
      cards: [
        { label: 'Build your next range', href: '/auth?mode=signup' },
        { label: 'Book a merchandising demo', href: '/contact' },
      ],
    },
  },
  'sourcing-supply-chain': {
    heroBody: 'Protect margin. Prevent delays. Keep every order moving.',
    composerPlaceholder: 'What are you sourcing or trying to keep on track?',
    useCases: [
      {
        label: 'Compare Suppliers and Quotes',
        prompt:
          'Compare suppliers and quotes on a consistent basis. Normalize specifications, quantities, currencies, trade terms, lead times, quality requirements, and payment terms, then identify unresolved differences. Ask for product specifications, quantities, delivery location, target costs, supplier information, and required dates before building the sourcing plan. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Hit the Target Cost',
        prompt:
          'Find practical ways to reach my target product cost. Compare materials, construction, packaging, quantities, and supplier options while showing quality, timing, and commercial tradeoffs. Ask for product specifications, quantities, delivery location, target costs, supplier information, and required dates before building the sourcing plan. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Confirm Capacity and Lead Times',
        prompt:
          'Build a supplier capacity and lead-time verification plan. Map production stages, material availability, bottlenecks, and required evidence, then identify what needs supplier confirmation. Ask for product specifications, quantities, delivery location, target costs, supplier information, and required dates before building the sourcing plan. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Track Production Milestones',
        prompt:
          'Create a production milestone tracker for my order. Include material approvals, sampling, production, inspection, packing, and shipment, with owners, dependencies, planned dates, and escalation points. Ask for product specifications, quantities, delivery location, target costs, supplier information, and required dates before building the sourcing plan. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Flag Delivery Risks Early',
        prompt:
          'Assess delivery risks in my sourcing plan. Identify late materials, approval delays, capacity constraints, and transport dependencies, then prioritize mitigation actions and early warning signals. Ask for product specifications, quantities, delivery location, target costs, supplier information, and required dates before building the sourcing plan. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Build Backup Sourcing Plans',
        prompt:
          'Develop backup sourcing options for my product. Compare alternative suppliers and materials, qualification steps, switching costs, and lead times, and define triggers for activating each option. Ask for product specifications, quantities, delivery location, target costs, supplier information, and required dates before building the sourcing plan. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
    ],
    whyCards: [
      {
        title: 'Protect Margin Before Commitment',
        body: 'Understand the commercial impact of supplier, material, volume, MOQ, origin, freight, and lead-time decisions.',
        imageAlt:
          'Supplier, material, MOQ, freight, and margin scenarios compared',
      },
      {
        title: 'Keep Production on Track',
        body: 'Give sourcing, product, suppliers, and supply-chain teams one view of approvals, outstanding work, and next actions.',
        imageAlt: 'A production critical path with approvals and milestones',
      },
      {
        title: 'Respond Before Delivery Slips',
        body: 'Identify capacity, material, compliance, and production risks while there is still time to protect delivery.',
        imageAlt: 'Delivery risks and affected orders surfaced early',
      },
    ],
    insights: [
      {
        title: 'Supplier and Quote Comparison',
        body: 'Compare capability, quality, cost, MOQ, terms, capacity, lead time, location, compliance, and performance.',
      },
      {
        title: 'Production Critical Path',
        body: 'Track material approval, booking, sampling, production, inspection, and shipment readiness.',
      },
      {
        title: 'Risk and Backup Allocation',
        body: 'Understand downstream impact and compare alternate suppliers, materials, regions, quantities, and production windows.',
      },
    ],
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question:
            'Can we use our existing suppliers and commercial information?',
          answer:
            'Yes. Organize your supplier base, quotes, capabilities, costs, MOQs, lead times, capacity, compliance, and performance around your criteria.',
        },
        {
          question: 'Can IMAI compare total cost instead of unit price alone?',
          answer:
            'Yes. Scenarios can include materials, trims, labor, packaging, tooling, MOQ, freight, duty, tariff, and other assumptions.',
        },
        {
          question:
            'Does IMAI replace our PLM, ERP, or order-management system?',
          answer:
            'No. Those remain official systems of record while IMAI helps teams interpret information, compare scenarios, identify risk, and decide what to do next.',
        },
        {
          question: 'Does IMAI verify suppliers and compliance?',
          answer:
            'No. It can organize capabilities, certifications, audits, tests, origin, and missing records. Formal approval remains with authorized teams.',
        },
      ],
    },
    cta: {
      eyebrow: 'Supplier, cost, capacity, timeline, and risk in view',
      heading: 'Protect margin and delivery.',
      cards: [
        { label: 'Build Your Sourcing Plan', href: '/auth?mode=signup' },
        { label: 'Book a Sourcing Demo', href: '/contact' },
      ],
    },
  },
  'manufactures-factories': {
    heroBody: 'Win more orders. Protect margin. Ship on time.',
    composerPlaceholder: 'What buyer request are you working on?',
    useCases: [
      {
        label: 'Convert Enquiries into Orders',
        prompt:
          'Turn a buyer enquiry into an order-development plan. Identify missing specifications, assess commercial fit, and prepare clarification questions, quotation inputs, sampling steps, and follow-up priorities. Ask for the buyer brief, technical files, order quantities, factory capabilities, costs, and shipment deadline before planning the work. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Confirm Feasibility Before Quoting',
        prompt:
          'Check manufacturing feasibility before issuing a quote. Review materials, machinery, construction, tolerances, testing, and capacity, and separate confirmed capabilities from assumptions requiring trials. Ask for the buyer brief, technical files, order quantities, factory capabilities, costs, and shipment deadline before planning the work. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Build Costings Without Guesswork',
        prompt:
          'Build an itemized factory costing from the supplied specifications. Include material consumption, wastage, labor, overhead, packaging, and margin, and label unknown inputs instead of guessing. Ask for the buyer brief, technical files, order quantities, factory capabilities, costs, and shipment deadline before planning the work. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Reduce Sample Approval Rounds',
        prompt:
          'Reduce avoidable sample approval rounds. Consolidate buyer comments, prioritize issues, define measurable acceptance criteria, and prepare a revision checklist with responsibilities and review dates. Ask for the buyer brief, technical files, order quantities, factory capabilities, costs, and shipment deadline before planning the work. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Lock Changes Before Bulk',
        prompt:
          'Prepare a pre-production change lock for bulk manufacturing. Reconcile approved samples, specifications, materials, colors, and packaging, then list unresolved decisions and required sign-offs. Ask for the buyer brief, technical files, order quantities, factory capabilities, costs, and shipment deadline before planning the work. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Protect Margin and Shipment',
        prompt:
          'Review an order for margin and shipment risks. Compare quoted and current costs, change requests, production progress, and delivery commitments, then recommend actions to protect both. Ask for the buyer brief, technical files, order quantities, factory capabilities, costs, and shipment deadline before planning the work. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
    ],
    whyCards: [
      {
        title: 'Win More Production Orders',
        body: 'Respond quickly with a product the factory can make profitably and deliver reliably.',
        imageAlt: 'A feasible product proposal with costing and delivery dates',
      },
      {
        title: 'Reduce Development Waste',
        body: 'Remove unclear information and repeated decisions that create unnecessary work.',
        imageAlt: 'Buyer comments and product revisions connected in one place',
      },
      {
        title: 'Protect Margin and Repeat Business',
        body: 'Control changes, production risk, quality, and delivery from order confirmation through shipment.',
        imageAlt:
          'Cost, readiness, quality, and shipment risks reviewed together',
      },
    ],
    insights: [
      {
        title: 'Production Feasibility and Costing',
        body: 'Check construction, materials, machinery, MOQ, capacity, timing, consumption, labor, packaging, wastage, and overhead.',
      },
      {
        title: 'Sample and Change Control',
        body: 'Track every sample, buyer comment, correction, approval, and impact on cost, material, and delivery.',
      },
      {
        title: 'Quality and Shipment Readiness',
        body: 'Confirm materials, approvals, capacity, inspection findings, quantities, packing, documents, and release requirements.',
      },
    ],
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Can we use our existing templates?',
          answer:
            'Yes. Product information can follow your tech-pack formats, terminology, measurements, and buyer approval requirements.',
        },
        {
          question: 'Can we use our own costs and production data?',
          answer:
            'Yes. Costings and scenarios can use your material prices, consumption, labor, processes, wastage, overhead, capacity, and efficiency assumptions.',
        },
        {
          question: 'How are buyer changes controlled?',
          answer:
            'Each revision is recorded against the relevant product, sample, material, or specification while the current approved version stays clear.',
        },
        {
          question: 'Does IMAI replace our factory systems?',
          answer:
            'No. ERP, MES, PLM, inventory, quality, and financial systems remain official records while IMAI connects product development and production inputs.',
        },
      ],
    },
    cta: {
      eyebrow: 'Better orders. Less factory waste.',
      heading: 'Turn the next buyer brief into a production-ready order.',
      cards: [
        { label: 'Review a Buyer Request', href: '/auth?mode=signup' },
        { label: 'Book a Factory Demo', href: '/contact' },
      ],
    },
  },
} as const satisfies Record<FinalRoleSlug, MarketingRolePageSource>;
